import React, { useState, useEffect } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { X, Plus, ChevronDown, AlertCircle } from 'lucide-react';


export interface MappingConfiguration {
  nestingaleAttribute: string;
  supplierAttributes: string[];
}

interface MappingTableProps {
  supplierHeaders: string[];
  onSubmit: (mappings: MappingConfiguration[]) => void;
  vendorName?: string;
  isSubmitting?: boolean;
}

interface NestingaleAttributesResponse {
  nestingale: {
    [groupName: string]: string[];
  };
  required: string[];
  optional: string[];
  nestingaleMap?: {
    [key: string]: string | string[];
  };
}

// Function to categorize supplier attributes with improved grouping for numbered items
const categorizeSupplierHeaders = (headers: string[]) => {
  const categories: Record<string, string[]> = {};

  headers.forEach(header => {
    const lowerHeader = header.toLowerCase();
    let categoryName = 'Other';

    // Check for specific categories first
    if (lowerHeader.includes('image') || lowerHeader.includes('img') || lowerHeader.includes('photo') || lowerHeader.includes('picture')) {
      categoryName = 'Image';
    } else if (lowerHeader.includes('product') || lowerHeader.includes('item') || lowerHeader.includes('sku') || lowerHeader.includes('id')) {
      categoryName = 'Product';
    } else if (lowerHeader.includes('price') || lowerHeader.includes('cost') || lowerHeader.includes('amount') || lowerHeader.includes('value')) {
      categoryName = 'Price';
    } else if (lowerHeader.includes('description') || lowerHeader.includes('detail') || lowerHeader.includes('summary') || lowerHeader.includes('info')) {
      categoryName = 'Description';
    } else if (lowerHeader.includes('silo')) {
      categoryName = 'Silo';
    } else {
      // Check if this is a numbered variation of an existing category
      // Remove numbers and common separators to find the base name
      const baseHeader = header.replace(/[\s\-_]*\d+$/i, '').trim();
      const baseHeaderLower = baseHeader.toLowerCase();
      
      // Check if the base name matches any known category patterns
      if (baseHeaderLower.includes('image') || baseHeaderLower.includes('img') || baseHeaderLower.includes('photo') || baseHeaderLower.includes('picture')) {
        categoryName = 'Image';
      } else if (baseHeaderLower.includes('product') || baseHeaderLower.includes('item') || baseHeaderLower.includes('sku') || baseHeaderLower.includes('id')) {
        categoryName = 'Product';
      } else if (baseHeaderLower.includes('price') || baseHeaderLower.includes('cost') || baseHeaderLower.includes('amount') || baseHeaderLower.includes('value')) {
        categoryName = 'Price';
      } else if (baseHeaderLower.includes('description') || baseHeaderLower.includes('detail') || baseHeaderLower.includes('summary') || baseHeaderLower.includes('info')) {
        categoryName = 'Description';
      } else if (baseHeaderLower.includes('silo')) {
        categoryName = 'Silo';
      } else if (baseHeader && baseHeader !== header) {
        // If we removed numbers and got a different string, use the base as category
        categoryName = baseHeader;
      }
    }

    if (!categories[categoryName]) {
      categories[categoryName] = [];
    }
    categories[categoryName].push(header);
  });

  // Filter out empty categories and return as array of [name, items] pairs
  return Object.entries(categories).filter(([, items]) => items.length > 0);
};

const SupplierAttributeSelector: React.FC<{
  availableHeaders: string[];
  selectedAttributes: string[];
  onAttributeToggle: (attribute: string) => void;
  onCategorySelectAll: (category: string[]) => void;
}> = ({ availableHeaders, selectedAttributes, onAttributeToggle, onCategorySelectAll }) => {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const categorizedHeaders = categorizeSupplierHeaders(availableHeaders);

  const filteredHeaders = availableHeaders.filter((header) =>
    header.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleSelectAll = (categoryItems: string[]) => {
    onCategorySelectAll(categoryItems);
    setOpen(false);
  };

  const handleAttributeToggle = (attribute: string) => {
    onAttributeToggle(attribute);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-8"
        >
          <div className="flex items-center gap-1">
            <Plus size={12} />
            <span className="text-sm">Add supplier attribute</span>
          </div>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-white z-50" align="start">
        <Command>
          <CommandInput
            placeholder="Search attributes..."
            onValueChange={setSearchValue}
          />
          {/* disable built-in filtering here */}
          <CommandList className='filter-none'>
            <CommandEmpty>No attributes found.</CommandEmpty>

            {searchValue ? (
              <CommandGroup>
                {filteredHeaders.map((header) => (
                  <CommandItem
                    key={header}
                    onSelect={() => handleAttributeToggle(header)}
                  >
                    {header}
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              categorizedHeaders.map(([categoryName, categoryItems]) => (
                <CommandGroup key={categoryName} heading={categoryName}>
                  <CommandItem
                    onSelect={() => handleSelectAll(categoryItems)}
                    className="font-medium text-primary"
                  >
                    <Plus size={16} />
                    <span>Select all {categoryName}</span>
                  </CommandItem>
                  {categoryItems.map((header) => (
                    <CommandItem
                      key={header}
                      onSelect={() => handleAttributeToggle(header)}
                      className="ml-4"
                    >
                      {header}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};


const MappingTable: React.FC<MappingTableProps> = ({ supplierHeaders, onSubmit, vendorName, isSubmitting: parentIsSubmitting }) => {
  const [nestingaleGroups, setNestingaleGroups] = useState<{[groupName: string]: string[]}>({});
  const [requiredAttributes, setRequiredAttributes] = useState<string[]>([]);
  const [optionalAttributes, setOptionalAttributes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [mappings, setMappings] = useState<MappingConfiguration[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Define colors for different attribute groups using standard Tailwind gray shades
  const groupColors = {
    'standard': 'border-l-4 border-l-emerald-300',
    'image': 'border-l-4 border-l-blue-300',
    'color': 'border-l-4 border-l-purple-300',
    'dimentions': 'border-l-4 border-l-green-300',
    'material': 'border-l-4 border-l-yellow-300',
    'features': 'border-l-4 border-l-red-300',
    'shipping': 'border-l-4 border-l-teal-300',
    'shopify': 'border-l-4 border-l-indigo-300',
    'variants': 'border-l-4 border-l-pink-300',
    'style': 'border-l-4 border-l-orange-300',
    'productType': 'border-l-4 border-l-cyan-300',
    'additional': 'border-l-4 border-l-gray-300',
    'default': ''
  };

  const getGroupColor = (groupName: string): string => {
    return groupColors[groupName as keyof typeof groupColors] || groupColors.default;
  };

  const getGroupHeaderColor = (groupName: string): string => {
    // Map group names to their corresponding background colors and borders
    const headerColors: {[key: string]: string} = {
      'standard': 'bg-emerald-50 border-l-4 border-l-emerald-300 hover:bg-emerald-50',
      'image': 'bg-blue-50 border-l-4 border-l-blue-300 hover:bg-blue-50',
      'color': 'bg-purple-50 border-l-4 border-l-purple-300 hover:bg-purple-50',
      'dimentions': 'bg-green-50 border-l-4 border-l-green-300 hover:bg-green-50',
      'material': 'bg-yellow-50 border-l-4 border-l-yellow-300 hover:bg-yellow-50',
      'features': 'bg-red-50 border-l-4 border-l-red-300 hover:bg-red-50',
      'shipping': 'bg-teal-50 border-l-4 border-l-teal-300 hover:bg-teal-50',
      'shopify': 'bg-indigo-50 border-l-4 border-l-indigo-300 hover:bg-indigo-50',
      'variants': 'bg-pink-50 border-l-4 border-l-pink-300 hover:bg-pink-50',
      'style': 'bg-orange-50 border-l-4 border-l-orange-300 hover:bg-orange-50',
      'productType': 'bg-cyan-50 border-l-4 border-l-cyan-300 hover:bg-cyan-50',
      'additional': 'bg-gray-50 border-l-4 border-l-gray-300 hover:bg-gray-50',
      'default': 'bg-gray-50 border-l-4 border-l-gray-300 hover:bg-gray-50'
    };
    return headerColors[groupName] || headerColors.default;
  };

  // Group mappings by their attribute groups
  const groupedMappings = mappings.reduce((acc, mapping) => {
    // Find which group this attribute belongs to
    let groupName = 'Other';
    for (const [group, attributes] of Object.entries(nestingaleGroups)) {
      if (attributes.includes(mapping.nestingaleAttribute)) {
        groupName = group;
        break;
      }
    }
    
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push(mapping);
    return acc;
  }, {} as Record<string, typeof mappings>);

  // Filter out empty groups and ensure we have valid data
  const validGroupedMappings = Object.entries(groupedMappings)
    .filter(([groupName, groupMappings]) => groupMappings.length > 0)
    .reduce((acc, [groupName, groupMappings]) => {
      acc[groupName] = groupMappings;
      return acc;
    }, {} as Record<string, typeof mappings>);

  useEffect(() => {
    const fetchNestingaleAttributes = async () => {
      if (!vendorName) {
        console.log('No vendor selected, using default attributes');
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(
          `https://nestingale.com/v1/api/productFeedAttributes?vendor=${encodeURIComponent(vendorName)}`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch attributes: ${response.status}`);
        }
        
        const data: NestingaleAttributesResponse = await response.json();
        console.log('Fetched Nestingale attributes:', data);
        
        // Handle the new API structure
        if (data.nestingale && typeof data.nestingale === 'object') {
          // Clean the groups by filtering out empty strings and duplicates
          const cleanedGroups: {[groupName: string]: string[]} = {};
          
          Object.entries(data.nestingale).forEach(([groupName, groupAttributes]) => {
            // Filter out empty strings and duplicates
            const uniqueAttributes = [...new Set(groupAttributes.filter(attr => attr && attr.trim() !== ''))];
            if (uniqueAttributes.length > 0) {
              cleanedGroups[groupName] = uniqueAttributes;
            }
          });
          
          setNestingaleGroups(cleanedGroups);
          
          // Flatten all attributes from groups
          const allAttributes: string[] = [];
          Object.values(cleanedGroups).forEach(groupAttributes => {
            allAttributes.push(...groupAttributes);
          });

          // Use nestingaleMap from API if present to pre-populate mappings
          const nestingaleMap = data.nestingaleMap || {};
          const initialMappings = allAttributes.map(attr => {
            let supplierAttributes: string[] = [];
            if (nestingaleMap[attr]) {
              if (Array.isArray(nestingaleMap[attr])) {
                // Filter out supplier attributes that no longer exist in the current file
                supplierAttributes = (nestingaleMap[attr] as string[]).filter(supplierAttr => 
                  supplierHeaders.includes(supplierAttr)
                );
              } else if (typeof nestingaleMap[attr] === 'string') {
                // Only include the supplier attribute if it still exists in the current file
                const supplierAttr = nestingaleMap[attr] as string;
                if (supplierHeaders.includes(supplierAttr)) {
                  supplierAttributes = [supplierAttr];
                }
              }
            }
            return {
              nestingaleAttribute: attr,
              supplierAttributes,
            };
          });

          setMappings(initialMappings);
        } else {
          console.log('No nestingale groups found in API response');
          setNestingaleGroups({});
          setMappings([]);
        }
        
        // Set required and optional attributes (filter out empty strings)
        setRequiredAttributes((data.required || []).filter(attr => attr && attr.trim() !== ''));
        setOptionalAttributes((data.optional || []).filter(attr => attr && attr.trim() !== ''));
        
      } catch (error) {
        console.error('Error fetching Nestingale attributes:', error);
        // Fallback to default attributes on error
        const defaultGroups = {
          'standard': ['productSkuID', 'title', 'description', 'price', 'category'],
          'image': ['imageLink', 'additionalImageLink'],
          'color': ['colorOriginal'],
          'dimentions': ['productLength', 'productHeight', 'productWeight']
        };
        setNestingaleGroups(defaultGroups);
        setRequiredAttributes(['productSkuID', 'title', 'description', 'imageLink']);
        setOptionalAttributes(['additionalImageLink', 'colorOriginal']);
        
        const allDefaultAttributes = Object.values(defaultGroups).flat();
        const defaultMappings = allDefaultAttributes.map(attr => ({
          nestingaleAttribute: attr,
          supplierAttributes: []
        }));
        setMappings(defaultMappings);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNestingaleAttributes();
  }, [vendorName, supplierHeaders]);

  const toggleSupplierAttribute = (mappingIndex: number, supplierAttribute: string) => {
    const newMappings = [...mappings];
    const currentAttributes = newMappings[mappingIndex].supplierAttributes;
    
    if (currentAttributes.includes(supplierAttribute)) {
      // Remove attribute
      newMappings[mappingIndex].supplierAttributes = currentAttributes.filter(
        attr => attr !== supplierAttribute
      );
    } else {
      // Add attribute
      newMappings[mappingIndex].supplierAttributes = [...currentAttributes, supplierAttribute];
    }
    setMappings(newMappings);
  };

  const addMultipleSupplierAttributes = (mappingIndex: number, attributes: string[]) => {
    const newMappings = [...mappings];
    const currentAttributes = newMappings[mappingIndex].supplierAttributes;
    
    // Add only attributes that aren't already selected
    const newAttributes = attributes.filter(attr => !currentAttributes.includes(attr));
    newMappings[mappingIndex].supplierAttributes = [...currentAttributes, ...newAttributes];
    setMappings(newMappings);
  };

  const removeSupplierAttribute = (mappingIndex: number, supplierAttribute: string) => {
    const newMappings = [...mappings];
    newMappings[mappingIndex].supplierAttributes = newMappings[mappingIndex].supplierAttributes.filter(
      attr => attr !== supplierAttribute
    );
    setMappings(newMappings);
  };

  const getAvailableSupplierHeaders = (mappingIndex: number) => {
    return supplierHeaders.filter(header => 
      !mappings[mappingIndex].supplierAttributes.includes(header)
    );
  };

  const handleSubmit = async () => {
    if (!vendorName) {
      alert('Please select a vendor first');
      return;
    }

    // Check if all required attributes have mappings
    const unmappedRequiredAttributes = requiredAttributes.filter(requiredAttr => {
      const mapping = mappings.find(m => m.nestingaleAttribute === requiredAttr);
      return !mapping || mapping.supplierAttributes.length === 0;
    });

    if (unmappedRequiredAttributes.length > 0) {
      const missingAttributes = unmappedRequiredAttributes.map(attr => getNestingaleAttributeLabel(attr)).join(', ');
      alert(`Please map the following required attributes: ${missingAttributes}`);
      return;
    }

    const validMappings = mappings.filter(mapping => mapping.supplierAttributes.length > 0);
    
    if (validMappings.length === 0) {
      alert('Please map at least one supplier attribute to a Nestingale attribute');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call the parent's onSubmit function which will handle the file upload and mapping submission
      await onSubmit(validMappings);
      
    } catch (error) {
      console.error('Error submitting mapping configuration:', error);
      alert(`Failed to submit mapping configuration: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getNestingaleAttributeLabel = (key: string) => {
    // Convert camelCase to readable format
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  };

  const isRequiredAttribute = (attribute: string) => {
    return requiredAttributes.includes(attribute);
  };

  const isRequiredAttributeMissing = (attribute: string) => {
    if (!isRequiredAttribute(attribute)) return false;
    const mapping = mappings.find(m => m.nestingaleAttribute === attribute);
    return !mapping || mapping.supplierAttributes.length === 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
          <p className="text-sm text-muted-foreground">Loading Nestingale attributes...</p>
        </div>
      </div>
    );
  }

  // Check if we have any valid mappings to display
  if (Object.keys(validGroupedMappings).length === 0) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">No attributes available for mapping</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-1/4">Group</TableHead>
              <TableHead className="w-1/4">Nestingale Attributes</TableHead>
              <TableHead className="w-1/2">Supplier Attributes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.entries(validGroupedMappings)
              .map(([groupName, groupMappings]) => (
                <React.Fragment key={`group-${groupName}`}>
                  {/* Group mappings */}
                  {groupMappings.map((mapping, index) => {
                    const mappingIndex = mappings.findIndex(m => m.nestingaleAttribute === mapping.nestingaleAttribute);
                    const isRequired = isRequiredAttribute(mapping.nestingaleAttribute);
                    const isFirstInGroup = index === 0;
                    
                    // Safety check: if mappingIndex is -1, skip this mapping
                    if (mappingIndex === -1) {
                      console.warn(`Mapping not found for attribute: ${mapping.nestingaleAttribute}`);
                      return null;
                    }
                    
                    return (
                      <TableRow 
                        key={`${groupName}-${mapping.nestingaleAttribute}`} 
                        className={`${getGroupColor(groupName)} ${isRequiredAttributeMissing(mapping.nestingaleAttribute) ? 'bg-red-50 border-l-4 border-l-red-500' : ''}`}
                      >
                        <TableCell className="font-medium">
                          {isFirstInGroup && (
                            <div className="text-sm font-semibold text-gray-700">
                              {groupName.charAt(0).toUpperCase() + groupName.slice(1)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {getNestingaleAttributeLabel(mapping.nestingaleAttribute)}
                            {isRequired && (
                              <Badge variant={isRequiredAttributeMissing(mapping.nestingaleAttribute) ? "destructive" : "secondary"} className="text-xs px-1 py-0 h-4">
                                <AlertCircle size={10} className="mr-1" />
                                {isRequiredAttributeMissing(mapping.nestingaleAttribute) ? 'Required (Missing)' : 'Required'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-2">
                            {getAvailableSupplierHeaders(mappingIndex).length > 0 && (
                              <SupplierAttributeSelector
                                availableHeaders={getAvailableSupplierHeaders(mappingIndex)}
                                selectedAttributes={mapping.supplierAttributes}
                                onAttributeToggle={(attribute) => toggleSupplierAttribute(mappingIndex, attribute)}
                                onCategorySelectAll={(categoryItems) => addMultipleSupplierAttributes(mappingIndex, categoryItems)}
                              />
                            )}
                            <div className="flex flex-wrap gap-1">
                              {mapping.supplierAttributes.map((attr) => (
                                <Badge key={`${mapping.nestingaleAttribute}-${attr}`} variant="secondary" className="text-xs">
                                  {attr}
                                  <button
                                    onClick={() => removeSupplierAttribute(mappingIndex, attr)}
                                    className="ml-1 hover:text-destructive"
                                  >
                                    <X size={12} />
                                  </button>
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              ))}
          </TableBody>
        </Table>
      </div>
      
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          <p>Required attributes are marked with a red badge. Missing required attributes are highlighted in red.</p>
          <p>Total mappings: {mappings.filter(m => m.supplierAttributes.length > 0).length}</p>
          {(() => {
            const missingRequired = requiredAttributes.filter(attr => {
              const mapping = mappings.find(m => m.nestingaleAttribute === attr);
              return !mapping || mapping.supplierAttributes.length === 0;
            });
            if (missingRequired.length > 0) {
              return (
                <p className="text-red-600 font-medium">
                  Missing required mappings: {missingRequired.map(attr => getNestingaleAttributeLabel(attr)).join(', ')}
                </p>
              );
            }
            return null;
          })()}
        </div>
        <Button 
          onClick={handleSubmit} 
          disabled={
            isSubmitting || 
            parentIsSubmitting || 
            mappings.filter(m => m.supplierAttributes.length > 0).length === 0 ||
            requiredAttributes.some(requiredAttr => {
              const mapping = mappings.find(m => m.nestingaleAttribute === requiredAttr);
              return !mapping || mapping.supplierAttributes.length === 0;
            })
          }
          className="ml-auto"
        >
          {isSubmitting || parentIsSubmitting ? 'Submitting...' : 'Submit Mapping'}
        </Button>
      </div>
    </div>
  );
};

export default MappingTable;
